import { use, useCallback, useEffect, useMemo, useRef } from 'react';

import path from 'path';

import { Stack, Typography } from '@mui/material';
import {
	GridCallbackDetails,
	GridColDef,
	GridRowSelectionModel,
	useGridApiRef,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ParamKeyValuePair, useSearchParams } from 'react-router-dom';
import useSWR, { KeyedMutator } from 'swr';

import {
	ActionButton,
	Card,
	DataGrid,
	DataGridToolbar,
	MultiSelectBar,
	type RowAction,
} from '~components';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import useColDefs from '~components/dataGrids/useColDefs';
import VectorImage from '~components/media/vectorImage';
import { generalInformation, pagePathSegments, queryParameters } from '~constants';
import { DataGridToolBarFilterType, StatusVariant, UserRoleEnum } from '~enums';
import SelectedDevicesContext from '~features/devices/providers/selectedDevicesContext';
import useAuthorize from '~hooks/useAuthorize';
import { OffsetPagedResults } from '~interfaces';
import Device, { deviceGridCols } from '~interfaces/device';
import DeviceListType from '~interfaces/deviceListType';
import { cancellationMiddleware } from '~lib/swr';
import { getService } from '~services/serviceFactory';

import Dimens from '../../../../themes/dimens';
import DevicesService from '../../services/devicesService';

const service = getService(DevicesService);
const persistenceKey = 'devices';

interface DevicesDataGridProps {
	showExportButton?: boolean;
	enableCreateDevice?: boolean;
	enableEditDevice?: boolean;
	enableDeleteDevice?: boolean;
	enableAssignDevice?: boolean;
	enableUnassignDevice?: boolean;
	showTitle: boolean;
	includeOrganisation?: boolean;
	includeResourceGroup?: boolean;
	enableMultiSelect?: boolean;
	listType: DeviceListType;
	overwrittenClientId?: string;
	/**
	 * @param mutate Mutate object that's to be called to ensure the data grid is updated.
	 * @param clearSelection Call to clear the datagrid selection.
	 */
	onAssignDevices?: (
		mutate: KeyedMutator<OffsetPagedResults<Device>>,
		clearSelection: () => void
	) => void;
	/**
	 * @param mutate Mutate object that's to be called to ensure the data grid is updated.
	 * @param clearSelection Call to clear the datagrid selection.
	 */
	onUnassignDevices?: (
		mutate: KeyedMutator<OffsetPagedResults<Device>>,
		clearSelection: () => void
	) => void;
	onChangeResourceGroupForDevices?: (
		mutate: KeyedMutator<OffsetPagedResults<Device>>,
		clearSelection: () => void
	) => void;
	handleExportButtonClicked?: () => void;
}

const DevicesDataGrid = ({
	listType: devicesListType = 'assigned',
	onAssignDevices,
	onUnassignDevices,
	onChangeResourceGroupForDevices,
	showExportButton = false,
	handleExportButtonClicked,
	overwrittenClientId,
	enableUnassignDevice,
	enableAssignDevice,
	showTitle = false,
	enableMultiSelect = false,
	includeOrganisation = false,
	includeResourceGroup = false,
}: DevicesDataGridProps) => {
	const { t } = useTranslation();
	const apiRef = useGridApiRef();
	const [searchParams] = useSearchParams();
	const selectedDevicesContext = use(SelectedDevicesContext);
	const auth = useAuthorize();
	const isSuperAdmin = auth.authorize([UserRoleEnum.SuperAdmin]);

	const includeOrganisationIdInLink =
		devicesListType == 'unassigned' ||
		devicesListType == 'assignedInternal' ||
		overwrittenClientId != undefined;

	const isInternal = devicesListType == 'assignedInternal';

	const resourceGroupFilterOrganisationId =
		isInternal ? searchParams.get(queryParameters.OrganisationId) : overwrittenClientId;
	const deviceCacheRef = useRef<Map<string, Device>>(new Map());

	const {
		data,
		error,
		isLoading,
		isValidating,
		mutate: mutateDevices,
	} = useSWR(
		service.getDevicesCacheKey(searchParams, overwrittenClientId, devicesListType),
		(_args: (string | URLSearchParams | undefined)[], options: { signal?: AbortSignal }) => {
			const page = searchParams.get(queryParameters.Page) ?? '1';
			const pageSize = searchParams.get(queryParameters.PageSize) ?? '10';
			const searchQuery = searchParams.get(queryParameters.SearchQuery) ?? undefined;
			const deviceModelName = searchParams.get(queryParameters.DeviceModelName) ?? undefined;
			const resourceGroupId = searchParams.get(queryParameters.ResourceGroupId) ?? undefined;
			const filterOrganisationId = searchParams.get(queryParameters.OrganisationId) ?? undefined;
			const externalOverwrittenClientId =
				devicesListType === 'unassigned' ?
					generalInformation.manufacturingOrganisationId
				:	overwrittenClientId;

			const organisationId = isInternal ? filterOrganisationId : externalOverwrittenClientId;

			return service.getDevices(
				{
					page: page,
					pageSize: pageSize,
					searchQuery: searchQuery,
					deviceModelName: deviceModelName,
					resourceGroupId: resourceGroupId,
					organisationId: organisationId,
				},
				options.signal
			);
		},
		{
			keepPreviousData: false,
			use: [cancellationMiddleware],
		}
	);

	// Derive the footer row count straight from the fetched data instead of a
	// separate state synced via `onSuccess`. The previous value is retained
	// while a new page is loading (`data?.total` momentarily undefined) so the
	// count never desyncs from the rendered rows or flickers to "0-0 of 0".
	const rowCountRef = useRef<number>(0);
	if (data?.total !== undefined) {
		rowCountRef.current = data.total;
	}
	const totalCount = rowCountRef.current;

	useEffect(() => {
		if (!data?.results) return;

		for (const device of data.results) {
			deviceCacheRef.current.set(device.id, device);
		}
	}, [data]);

	useEffect(() => {
		apiRef.current?.setRowSelectionModel({
			type: 'include',
			ids: new Set(selectedDevicesContext.selectedDevices.map((device) => device.id)),
		});
	}, [selectedDevicesContext.selectedDevices]);

	const clearSelection = useCallback(() => {
		// sets id 0 to unselected, and then resets the rest of the rows with the second flag
		apiRef?.current?.selectRow(0, false, true);
	}, [apiRef]);

	const handleRowSelectionModelChange = useCallback(
		(model: GridRowSelectionModel, details: GridCallbackDetails) => {
			// Missing reason means it was our own intervention, and we can ignore it to prevent infinite loop
			if (!details.reason) return;

			const newKeys = Array.from(model.ids)
				.map((id) => deviceCacheRef.current.get(id.toString()))
				.filter(Boolean) as Device[];

			selectedDevicesContext.replaceSelectedDevices(newKeys);
		},
		[selectedDevicesContext]
	);

	const colDefs = useColDefs<Device>();
	const columns = useMemo((): GridColDef<Device>[] => {
		const getOrganisationQueryParams = (value: Device) =>
			includeOrganisationIdInLink ?
				([
					[
						queryParameters.OverwrittenClientId,
						overwrittenClientId ??
							value.organisationId ??
							generalInformation.manufacturingOrganisationId,
					],
				] as ParamKeyValuePair[])
			:	([] as ParamKeyValuePair[]);

		return [
			colDefs.id(
				path.join(pagePathSegments.Devices, ':id', pagePathSegments.General),
				getOrganisationQueryParams
			),
			colDefs.gridColDefFor(deviceGridCols.deviceNameAndId, getOrganisationQueryParams),
			colDefs.deviceModel(),
			colDefs.gridColDefFor(deviceGridCols.firmwareVersion),
			...(includeOrganisation ?
				[
					colDefs.gridColDefFor(
						deviceGridCols.organisationAndResourceGroup,
						getOrganisationQueryParams
					),
				]
			:	[colDefs.resourceGroup()]),
			colDefs.gridColDefFor(deviceGridCols.battery),
			colDefs.gridColDefFor(deviceGridCols.lastOnline),
			...(enableUnassignDevice || enableAssignDevice ?
				[
					colDefs.rowActions((row) => {
						const actions: RowAction[] = [];
						const currentDevice = deviceCacheRef.current.get(row.id.toString());
						if (enableUnassignDevice) {
							actions.push({
								key: 'unassign-device',
								variant: StatusVariant.Destructive,
								icon: <VectorImage name='icon/unlink' />,
								label: t('unassign', { ns: 'actions' }),
								onClick: () => {
									selectedDevicesContext.updateSingleDevice(currentDevice);
									onUnassignDevices?.(mutateDevices, clearSelection);
								},
							});
						}
						if (enableAssignDevice) {
							actions.push({
								key: 'assign-device',
								icon: <VectorImage name='icon/link' />,
								label: t('assign', { ns: 'actions' }),
								onClick: () => {
									selectedDevicesContext.updateSingleDevice(currentDevice);
									onAssignDevices?.(mutateDevices, clearSelection);
								},
							});
						}

						const enableChangeResourceGroup =
							isSuperAdmin &&
							currentDevice?.organisationId !== generalInformation.manufacturingOrganisationId &&
							onChangeResourceGroupForDevices !== undefined &&
							enableUnassignDevice;

						if (enableChangeResourceGroup) {
							actions.push({
								key: 'change-resource-group-for-device',
								icon: <VectorImage name='icon/swap' />,
								label: t('changeResourceGroup', { ns: 'actions' }),
								onClick: () => {
									selectedDevicesContext.updateSingleDevice(currentDevice);
									onChangeResourceGroupForDevices?.(mutateDevices, clearSelection);
								},
							});
						}
						return actions;
					}),
				]
			:	[]),
		];
	}, [
		colDefs,
		includeOrganisation,
		includeResourceGroup,
		enableUnassignDevice,
		enableAssignDevice,
		includeOrganisationIdInLink,
		overwrittenClientId,
		t,
		onUnassignDevices,
		mutateDevices,
		clearSelection,
		onAssignDevices,
	]);

	const multiSelectBarActions = [
		...(enableAssignDevice ?
			[
				<ActionButton
					key='multiAssign'
					startIcon={<VectorImage name='icon/link' />}
					label={t('assign', { ns: 'actions' })}
					style={ActionButtonStyle.Text}
					onClick={() => onAssignDevices?.(mutateDevices, clearSelection)}
				/>,
			]
		:	[]),
		...(isSuperAdmin && onChangeResourceGroupForDevices !== undefined && enableUnassignDevice ?
			[
				<ActionButton
					key='multiChangeResourceGroup'
					startIcon={<VectorImage name='icon/swap' />}
					label={t('changeResourceGroup', { ns: 'actions' })}
					style={ActionButtonStyle.Text}
					variant={StatusVariant.Neutral}
					onClick={() => onChangeResourceGroupForDevices?.(mutateDevices, clearSelection)}
				/>,
			]
		:	[]),
		...(enableUnassignDevice ?
			[
				<ActionButton
					key='multiUnassign'
					startIcon={<VectorImage name='icon/unlink' />}
					label={t('unassign', { ns: 'actions' })}
					style={ActionButtonStyle.Text}
					variant={StatusVariant.Destructive}
					onClick={() => onUnassignDevices?.(mutateDevices, clearSelection)}
				/>,
			]
		:	[]),
	];

	const actionBarFilters = [
		{
			type: DataGridToolBarFilterType.Search,
		},
		{
			type: DataGridToolBarFilterType.DeviceModel,
		},
		...(isInternal ?
			[
				{
					type: DataGridToolBarFilterType.Organisation,
				},
			]
		:	[]),
		...(isInternal || includeResourceGroup ?
			[
				{
					type: DataGridToolBarFilterType.ResourceGroup,
					organisationId: resourceGroupFilterOrganisationId ?? undefined,
					isInternal: isInternal,
				},
			]
		:	[]),
	];
	const actionBarActions = [
		...(showExportButton ?
			[
				{
					id: 'export',
					label: t('export', { ns: 'actions' }),
					variant: StatusVariant.Brand,
					onClick: handleExportButtonClicked,
					icon: <VectorImage name='icon/export' />,
				},
			]
		:	[]),
	];

	return (
		<Card>
			<Stack spacing={Dimens.spacingXl}>
				{showTitle && (
					<Typography variant='labelLg'>{t('device.labels.plural', { ns: 'entities' })}</Typography>
				)}
				{devicesListType == 'assignedInternal' && (
					<Typography variant='labelLg'>
						{/* TODO: Temporary solution, because now we can't show assigned keys */}
						{/* {t('device.labels.assignedDevices', { ns: 'entities' })} */}
						{t('general.all', { ns: 'entities' })}{' '}
						{t('device.labels.plural', { ns: 'entities' }).toLowerCase()}
					</Typography>
				)}
				{devicesListType == 'unassigned' && (
					<Typography variant='labelLg'>
						{t('device.labels.unassignedDevices', { ns: 'entities' })}
					</Typography>
				)}

				<DataGridToolbar filters={actionBarFilters} actions={actionBarActions} />
				<DataGrid
					apiRef={apiRef}
					getRowId={(row) => row.id}
					persistenceKey={persistenceKey}
					editMode='row'
					getRowHeight={() => 'auto'}
					disableRowSelectionOnClick
					error={error != null}
					loading={isLoading || isValidating}
					columns={columns}
					rows={data?.results ?? []}
					rowCount={totalCount}
					checkboxSelection={enableMultiSelect}
					onRowSelectionModelChange={handleRowSelectionModelChange}
					disableRowSelectionExcludeModel
					keepNonExistentRowsSelected
					paginationModel={{
						page: Number(searchParams.get(queryParameters.Page)),
						pageSize: Number(searchParams.get(queryParameters.PageSize)),
					}}
					sx={{
						flexGrow: 1,
						width: 1,
						height: '100%',
						overflowX: 'auto',
						'& .MuiDataGrid-cell': {
							alignItems: 'center',
							display: 'flex',
						},
					}}
				/>
				{(selectedDevicesContext.selectedDevices?.length ?? 0) > 0 && (
					<MultiSelectBar
						label={t('xSelected', {
							ns: 'table',
							count: Number(selectedDevicesContext.selectedDevices?.length ?? 0),
						})}
						actions={multiSelectBarActions}
					/>
				)}
			</Stack>
		</Card>
	);
};
export default DevicesDataGrid;
